<template>
    <div class="supergroup-list-item" :class="itemClass" :id="supergroup.uuid">
      <table class="table table-hover mb-0">
        <thead class="visually-hidden">
          <tr>
            <th scope="col">Expand/Collapse</th>
            <th scope="col">Supergroup Name</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr :class="{'table-light': (hasChildren || hasDomains) && expanded}">
            <td style="width: 40px;" class="align-middle">
              <button 
                v-if="hasChildren || hasDomains" 
                @click="toggleExpand" 
                class="btn btn-sm btn-outline-secondary"
                :title="expanded ? 'Collapse' : 'Expand'"
                :aria-expanded="expanded.toString()"
              >
                <i v-if="expanded" class="fa fa-minus-square"></i>
                <i v-else class="fa fa-plus-square"></i>
              </button>
            </td>
            <td class="align-middle">
              <h4>
                {{ supergroup.name }}
                <span v-if="(supergroup.domain_count && supergroup.domain_count > 0) || (supergroup.child_count && supergroup.child_count > 0)" class="sub-header"> (
                  <span v-if="supergroup.child_count > 0">Child Supergroups: {{ supergroup.child_count }}</span>
                  <span v-if="supergroup.child_count > 0 && supergroup.domain_count > 0"> , </span>
                  <span v-if="supergroup.domain_count > 0">Child Domains: {{ supergroup.domain_count }}</span>
                )
                </span>
              </h4>
            </td>
            <td>
              <a href="#" @click.prevent="$emit('select', supergroup)" class="btn btn-sm btn-outline-info me-2">
                View Users
              </a>
              <button 
                @click="$emit('edit', supergroup)" 
                class="btn btn-sm btn-outline-primary me-2"
                title="Edit"
                :disabled="!canManageSupergroups"
              >
                <i class="fa fa-pencil"></i> Edit
              </button>
              <button 
                @click="$emit('delete', supergroup)" 
                class="btn btn-sm btn-outline-danger"
                title="Delete"
                :disabled="!canManageSupergroups"
              >
                <i class="fa fa-trash"></i> Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="expanded" class="nested-content">
        <!-- Child Supergroups -->
        <div v-if="hasChildren" class="nested-items">
          <SupergroupListItem
            ref="supergroupListItems"
            v-for="child in children"
            :key="child.uuid"
            :supergroup="child"
            :all-supergroups="allSupergroups"
            @edit="$emit('edit', $event)"
            @delete="$emit('delete', $event)"
            @select="$emit('select', $event)"
            :canManageSupergroups="canManageSupergroups"
          />
        </div>
        
        <!-- Associated Domains -->
        <div v-if="hasDomains" class="domains-section mt-2">
          <h5 class="domains-header">Associated Domains</h5>
          <div v-if="loadingDomains" class="text-center py-2">
            <div class="spinner-border spinner-border-sm text-primary">
              <span class="visually-hidden">Loading domains...</span>
            </div>
          </div>
          <div v-else-if="domainError" class="alert alert-danger py-1 px-2" role="alert">
            <i class="fa fa-exclamation-triangle me-1"></i>
            {{ domainError }}
          </div>
          <table v-else class="table table-sm domains-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">UUID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="domain in domains" :key="domain.domain_uuid">
                <td>{{ domain.name }}</td>
                <td><a :href="'#!/domains/' + domain.domain_uuid">{{ domain.domain_uuid }}</a></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'SupergroupListItem',
    props: {
      supergroup: {
        type: Object,
        required: true
      },
      allSupergroups: {
        type: Array,
        required: true
      },
      itemClass: {
        type: String,
        required: false
      },
      canManageSupergroups: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        expanded: false,
        domains: [],
        loadingDomains: false,
        domainError: null
      };
    },
    computed: {
      children() {
        return this.allSupergroups
          .filter(sg => sg.parent_id === this.supergroup.uuid)
          .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      },
      hasChildren() {
        return !!(this.supergroup && this.supergroup.child_count > 0);
      },
      hasDomains() {
        return this.supergroup.domain_count > 0 || this.domains.length > 0 || this.loadingDomains;
      },
      parent() {
        if (!this.supergroup.parent_id) {
          return null;
        }
        return this.$parent;
      }
    },
    methods: {
      toggleExpand() {
        this.expanded = !this.expanded;
        
        // Fetch domains when expanding if we haven't already
        if (this.expanded && !this.domains.length && !this.loadingDomains && !this.domainError) {
          this.fetchDomains();
        }
      },
      expand() {
        this.expanded = true;
        if (this.parent && this.parent.expand) {
          this.parent.expand();
        }
        if (this.expanded && !this.domains.length && !this.loadingDomains && !this.domainError) {
          this.fetchDomains();
        }
      },
      async fetchDomains() {
        // If no domain_ids, nothing to fetch
        if (!this.supergroup.domain_ids || this.supergroup.domain_ids.length === 0) {
          return;
        }
        
        this.loadingDomains = true;
        this.domainError = null;
        
        try {
          // Fetch all domains in a single batch request
          const response = await this.$angularWorld.adminEntitiesApi.getDomainsInSupergroupTree(this.supergroup.uuid);
          const domains = response.data || [];
          // Sort domains alphabetically by name
          this.domains = domains.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
        } catch (error) {
          console.error('Error fetching domains:', error);
          this.domainError = 'Failed to load domains. Please try again.';
        } finally {
          this.loadingDomains = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .supergroup-list-item {
    margin-bottom: 20px;
  }
  
  .nested-items, .domains-section {
    padding-left: 2rem;
    border-left: 1px solid #dee2e6;
    margin-left: 1.25rem;
  }

  .nested-items .supergroup-list-item {
    margin-bottom: 5px;
  }
  
  .domains-section {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }
  
  .domains-header {
    color: #495057;
    margin-bottom: 0.5rem;
    padding-left: 0.5rem;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 0.25rem;
  }
  
  .domains-table {
    margin-bottom: 0.5rem;
  }
  
  .domains-table th {
    font-weight: 500;
    color: #495057;
  }
  
  .table {
    margin-bottom: 0;
  }
  
  .table td {
    vertical-align: middle;
  }
  .table th:last-of-type, .table td:last-of-type {
    text-align: end;
  }
  .sub-header {
    padding-left: 6px;
    font-weight: 500;
    font-size: 1.2rem;
  }
  </style>